/**
 * Runtime polyfills for baseline JS methods missing on older browsers.
 *
 * Imported FIRST in `main.tsx` (before any app/vendor module that might call
 * these) so the patches are installed before any consumer runs. Keep this file
 * import-side-effect-only — no exports — so the ordering guarantee is obvious.
 *
 * Why this exists: the register page is where new users on arbitrary/old
 * browsers land. `Array.prototype.at` / `String.prototype.at` are unsupported
 * on Safari <15.4 and older Chromium/Samsung Internet, and a vendor chunk (or
 * our own code) calling `.at()` throws `"x.at is not a function"` there. The
 * project ships no core-js / plugin-legacy, so we install a tiny spec-correct
 * shim at runtime instead of pulling in heavy build tooling.
 */

// Spec-correct `.at(index)`: coerce the index to an integer, resolve negative
// indices from the end, and return `undefined` when out of range.
// See tc39.es/proposal-relative-indexing-method.
function at(this: { length: number; [k: number]: unknown }, index: number) {
  const len = this.length;
  // ToIntegerOrInfinity: NaN / non-integer coerces toward 0 / truncates.
  let relativeIndex = Math.trunc(Number(index)) || 0;
  if (relativeIndex < 0) relativeIndex += len;
  if (relativeIndex < 0 || relativeIndex >= len) return undefined;
  return this[relativeIndex];
}

if (!Array.prototype.at) {
  // eslint-disable-next-line no-extend-native
  Object.defineProperty(Array.prototype, 'at', {
    value: at,
    writable: true,
    enumerable: false,
    configurable: true,
  });
}

if (!String.prototype.at) {
  // eslint-disable-next-line no-extend-native
  Object.defineProperty(String.prototype, 'at', {
    value: at,
    writable: true,
    enumerable: false,
    configurable: true,
  });
}

// Browser page translation (Chrome "Translate this page", used by our NL/BR/FR/
// TR signups on /onboarding) swaps React's text nodes for <font> wrappers.
// When React later removes or inserts next to a node it still thinks it owns,
// the DOM throws "removeChild/insertBefore: not a child of this node" and the
// route error boundary blanks the page (Sentry CLEANLIST-PORTAL-9A). Skip the
// stale operation instead of throwing — the workaround from
// github.com/facebook/react/issues/11538. A no-op when nothing touched the DOM.
if (typeof Node === 'function' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T) {
    if (child.parentNode !== this) return child;
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ) {
    if (referenceNode && referenceNode.parentNode !== this) return newNode;
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
